import {
  Component,
  JSXElement,
  createSignal,
  onMount,
  onCleanup,
  Show,
  untrack,
} from 'solid-js';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
import MultiArrayDropdownOptions from '../MultiArrayDropdownOptions/MultiArrayDropdownOptions';
import type { MultiarrayDropdown } from '../MultiArrayDropdownOptions/MultiArrayDropdownOptions';
import Icon from '../Icon/Icon';
import { addComposedOutsideClickListener } from '../util';

export interface MADropdownSelectIconMenuProps {
  className?: string;
  dropDownOptions: MultiarrayDropdown[];
  tooltip?: string;
  iconPosition?: 'left' | 'right';
  selected: string[];
  closeOnScroll?: boolean;
  selectionChanged?(selected: string[]): void;
  onMenuOpen?(isOpen: boolean): void;
  commonIcon: string;
  showOnlyIconButton?: boolean;
  'data-cy'?: string;
}

const MADropdownSelectIconMenu: Component<MADropdownSelectIconMenuProps> = (
  props
): JSXElement => {
  const [isOpen, setIsOpen] = createSignal(false);
  let dropDownRef: HTMLDivElement | undefined;

  const handleScroll = (): void => {
    if (props.closeOnScroll) {
      setIsOpen(false);
      props.onMenuOpen?.(false);
    }
  };

  const handleToggle = (e: MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    const next = !isOpen();
    setIsOpen(next);
    props.onMenuOpen?.(next);
  };

  onMount(() => {
    const removeOutsideClickListener = addComposedOutsideClickListener(
      () => [dropDownRef],
      () => {
        untrack(() => {
          setIsOpen(false);
          props.onMenuOpen?.(false);
        });
      }
    );
    document.addEventListener('scroll', handleScroll, true);
    onCleanup(() => {
      removeOutsideClickListener();
      document.removeEventListener('scroll', handleScroll, true);
    });
  });

  return (
    <CustomTooltip text={props.tooltip ?? ''}>
      <div
        ref={dropDownRef}
        onClick={handleToggle}
        class={`dropdown-pane-container ${props.className ?? ''}`}
        data-testid={props['data-cy'] || 'ma-dropdown-select-icon-menu'}
      >
        <div class="name dropdownpane-link text-ellipsis">
          <Icon icon={props.commonIcon} size="icon-medium" />
          <Show when={!props.showOnlyIconButton}>
            <Icon
              icon={
                isOpen() ? 'tc-icon-arrow-drop-up' : 'tc-icon-arrow-drop-down'
              }
              size="icon-medium"
            />
          </Show>
        </div>
        <Show when={isOpen()}>
          <MultiArrayDropdownOptions
            isOpen={isOpen()}
            iconPosition={props.iconPosition}
            optionsType="both"
            multiarrayOptions={props.dropDownOptions}
            selected={props.selected}
            selectionChanged={props.selectionChanged}
          />
        </Show>
      </div>
    </CustomTooltip>
  );
};

export default MADropdownSelectIconMenu;
